import React, {useState, useMemo, useEffect, useCallback, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useSelector, useDispatch} from 'react-redux'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {selectUserData} from '../../../../app-store/selector'
import {
  resetProposalInfo,
  setProposalInfo,
} from '../../../../app-store/ProposalSlice'
import {proposalsApi} from './proposalsApi'
import {ProposalStats} from '../../components/ProposalStats'
import {ProposalFilters} from '../../components/ProposalFilters'
import {ProposalTable} from '../../components/ProposalTable'
import {ProposalPagination} from '../../components/ProposalPagination'
import {ProposalCreationComponent} from '../../components/proposal-creation'
import {ProposalPreview} from '../../components/ProposalPreview'
import {useProposalPaymentPreview} from '../ProposalBuilder/hooks/useProposalPaymentPreview'

import {
  ContentArea,
  MainArea,
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalBody,
  ModalActions,
  ModalCancelBtn,
  ModalDangerBtn,
  DuplicateModalBox,
  ModalOptionalNote,
  ModalFormGrid,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  ModalPrimaryBtn,
} from './styled.components'

dayjs.extend(utc)

// Bounds how long a single slow/hanging AD lookup can hold up the whole
// "Created By" resolution (and therefore the table render) in loadProposals.
const AD_LOOKUP_TIMEOUT_MS = 5000

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('AD lookup timed out')), ms),
    ),
  ])
}

const STATUS_MAP = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  ACCEPTED: 'Viewed',
  COMPLETED: 'Completed',
  EXPIRED: 'Expired',
  DECLINED: 'Declined',
}

function mapProposal(p) {
  return {
    id: p.proposalId || p.id,
    businessName: p.businessName || '',
    createdBy: p.salesRepId || p.createdBy || '',
    email: p.clientEmail || p.email || '',
    options: Array.isArray(p.options) ? p.options : [],
    created: p.createdAt
      ? dayjs.utc(p.createdAt).format('MMM DD, YYYY')
      : p.created || '',
    lastModified: p.updatedAt
      ? dayjs.utc(p.updatedAt).format('MMM DD, YYYY')
      : p.lastModified || '',
    // Only set once a proposal is sent — drafts have no expiry yet.
    expiration: p.expirationDate
      ? dayjs.utc(p.expirationDate).format('MMM DD, YYYY')
      : '',
    status:
      STATUS_MAP[p.statusCode] ||
      STATUS_MAP[p.status?.toUpperCase()] ||
      p.status ||
      'Draft',
    hasRevision: p.hasRevision || false,
    feedback: p.feedback || null,
    clientPhone: p.clientPhone || p.client_phone || '',
    thryvId: p.thryvId || p.thryv_id || '',
    clientType: (p.clientType || '').toUpperCase(),
  }
}

function ProposalToolDashboard({readOnly = false, allReps = false} = {}) {
  const client = useAuthClient()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {user} = useSelector(selectUserData)

  const [proposals, setProposals] = useState([])
  const [createdByNames, setCreatedByNames] = useState({})
  const [statusCounts, setStatusCounts] = useState({})
  const [totalProposals, setTotalProposals] = useState(0)
  const [loading, setLoading] = useState(false)

  // Filter / pagination / sort state
  const [activeTab, setActiveTab] = useState('all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false)
  const [sort, setSort] = useState([{column: 'createdAt', direction: 'desc'}])
  const [dateRange, setDateRange] = useState({startDate: '', endDate: ''})

  const [previewId, setPreviewId] = useState(null)
  const [previewPricingLoading, setPreviewPricingLoading] = useState(false)
  const [previewLoadingId, setPreviewLoadingId] = useState(null)
  const [editLoadingId, setEditLoadingId] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [confirmDuplicate, setConfirmDuplicate] = useState(null)
  const [duplicateForm, setDuplicateForm] = useState({
    businessName: '',
    clientEmail: '',
    clientPhone: '',
    thryvId: '',
    clientType: '',
  })

  const searchTimerRef = useRef(null)
  // Reports > Proposal Metrics passes allReps to see every rep's proposals;
  // the Proposal Tool's own dashboard stays scoped to the logged-in rep.
  const repId = allReps ? undefined : user?.email

  const {refreshPaymentPreview} = useProposalPaymentPreview()
  // Keep a stable ref so the effect below always calls the latest version of
  // refreshPaymentPreview without adding it to the effect's deps (which would
  // re-trigger after every hydration dispatch).
  const refreshRef = useRef(refreshPaymentPreview)
  useEffect(() => {
    refreshRef.current = refreshPaymentPreview
  })

  // After a proposal is loaded for preview, hydrate any backend-shape coupons
  // and recalculate per-item dueTodayAmount / option dueTodayTotal.
  useEffect(() => {
    if (!previewId) return
    setPreviewPricingLoading(true)
    refreshRef.current().finally(() => setPreviewPricingLoading(false))
  }, [previewId])

  // Proposal Metrics (allReps) shows a "Created By" column of the sales
  // rep's Azure AD display name rather than their raw email. Resolved once
  // per rep (cached by email in this ref) and awaited inside loadProposals
  // itself, before setProposals — so a page of rows only ever renders with
  // the display name already in hand, never a flash of the raw email first.
  const resolvedRepEmailsRef = useRef(new Set())

  const resolveCreatedByNames = useCallback(
    async mapped => {
      const emails = [...new Set(mapped.map(p => p.createdBy).filter(Boolean))]
      const toFetch = emails.filter(
        email => !resolvedRepEmailsRef.current.has(email),
      )
      if (toFetch.length === 0) return
      toFetch.forEach(email => resolvedRepEmailsRef.current.add(email))

      const pairs = await Promise.all(
        toFetch.map(async email => {
          try {
            const data = await withTimeout(
              client(
                `azuredirectory/getUserDetailsFromAD?userId=${encodeURIComponent(
                  email,
                )}`,
              ),
              AD_LOOKUP_TIMEOUT_MS,
            )
            return [email, data?.displayName || null]
          } catch (err) {
            // AD lookup failed or timed out — ProposalTable falls back to
            // the raw email (createdByNames[email] is falsy) for this rep.
            return [email, null]
          }
        }),
      )
      setCreatedByNames(prev => {
        const next = {...prev}
        pairs.forEach(([email, name]) => {
          next[email] = name
        })
        return next
      })
    },
    [client],
  )

  const loadProposals = useCallback(async () => {
    if (!allReps && !repId) return
    setLoading(true)
    try {
      const statusParam =
        activeTab !== 'all' ? activeTab.toUpperCase() : undefined
      const data = await proposalsApi.getProposals(client, {
        repId,
        page: currentPage,
        limit: perPage,
        status: statusParam,
        search: debouncedSearch || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        sort,
      })
      const raw = Array.isArray(data) ? data : data?.data || []
      const mapped = raw.map(mapProposal)
      if (allReps) await resolveCreatedByNames(mapped)
      setProposals(mapped)
      setStatusCounts(data?.statusCounts || {})
      setTotalProposals(data?.pagination?.total ?? raw.length)
    } catch (err) {
      setProposals([])
    } finally {
      setLoading(false)
    }
  }, [
    client,
    repId,
    allReps,
    activeTab,
    currentPage,
    perPage,
    debouncedSearch,
    dateRange,
    sort,
    resolveCreatedByNames,
  ])

  useEffect(() => {
    loadProposals()
  }, [loadProposals])

  const handleNewProposal = () => {
    dispatch(resetProposalInfo())
    setIsProposalModalOpen(true)
  }

  const handleTabChange = tab => {
    setActiveTab(tab)
    setCurrentPage(1)
  }

  const handleSearchChange = val => {
    setSearch(val)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1)
      setDebouncedSearch(val)
    }, 400)
  }

  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange({startDate, endDate})
    setCurrentPage(1)
  }

  const handleSort = apiKey => {
    setSort(prev => {
      const current = prev[0]
      const direction =
        current && current.column === apiKey && current.direction === 'asc'
          ? 'desc'
          : 'asc'
      return [{column: apiKey, direction}]
    })
    setCurrentPage(1)
  }

  const handlePerPageChange = value => {
    setPerPage(value)
    setCurrentPage(1)
  }

  const handleProposalModalOpenState = () => {
    setIsProposalModalOpen(false)
  }
  const handlePreview = async row => {
    setPreviewLoadingId(row.id)
    try {
      const data = await proposalsApi.getProposalById(client, row.id)
      dispatch(setProposalInfo(data))
      setPreviewId(row.id)
    } catch (err) {
      toast.error('Failed to load proposal preview.')
    } finally {
      setPreviewLoadingId(null)
    }
  }

  const handleEdit = async row => {
    setEditLoadingId(row.id)
    try {
      const data = await proposalsApi.getProposalById(client, row.id)
      dispatch(setProposalInfo(data))
      navigate('/proposal-tool/proposal-builder')
    } catch (err) {
      toast.error('Failed to load proposal.')
    } finally {
      setEditLoadingId(null)
    }
  }

  const handleDuplicate = row => {
    setDuplicateForm({
      businessName: row.businessName || '',
      clientEmail: row.email || '',
      clientPhone: row.clientPhone || '',
      thryvId: row.clientType === 'EXISTING' ? row.thryvId || '' : '',
      clientType: row.clientType || '',
    })
    setConfirmDuplicate(row)
  }

  const handleDuplicateConfirm = async () => {
    if (!confirmDuplicate) return
    const id = confirmDuplicate.id
    setConfirmDuplicate(null)
    try {
      await proposalsApi.duplicateProposal(client, id, duplicateForm)
      loadProposals()
      toast.success('Proposal duplicated successfully.')
    } catch (err) {
      const message =
        err?.message || err?.data?.message || 'Failed to duplicate proposal.'
      toast.error(message)
    }
  }

  const handleDuplicateFormChange = (field, value) => {
    setDuplicateForm(prev => ({...prev, [field]: value}))
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return
    const id = confirmDelete.id
    setConfirmDelete(null)
    try {
      await proposalsApi.deleteProposal(client, id)
      loadProposals()
    } catch (err) {
      // delete failed — leave list unchanged
    }
  }

  const tabCounts = useMemo(() => {
    const draft = statusCounts.DRAFT || 0
    const sent = statusCounts.SENT || 0
    const accepted = statusCounts.ACCEPTED || 0
    const completed = statusCounts.COMPLETED || 0
    const expired = statusCounts.EXPIRED || 0
    const declined = statusCounts.DECLINED || 0
    return {
      all: draft + sent + accepted + completed + expired + declined,
      draft,
      sent,
      accepted,
      completed,
      expired,
      declined,
    }
  }, [statusCounts])

  return (
    <ContentArea>
      {previewId && (
        <ProposalPreview
          overlay
          onBack={() => setPreviewId(null)}
          pricingLoading={previewPricingLoading}
        />
      )}
      <ProposalStats
        counts={{...tabCounts, total: tabCounts.all}}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
      <MainArea>
        <ProposalFilters
          searchValue={search}
          onSearchChange={handleSearchChange}
          onDateRangeChange={handleDateRangeChange}
        />

        <ProposalTable
          rows={proposals}
          loading={loading}
          sort={sort[0] || null}
          onSort={handleSort}
          onNewProposal={!readOnly ? handleNewProposal : undefined}
          onDuplicate={!readOnly ? handleDuplicate : undefined}
          onPreview={handlePreview}
          previewLoadingId={previewLoadingId}
          onEdit={!readOnly ? handleEdit : undefined}
          editLoadingId={editLoadingId}
          onDelete={!readOnly ? row => setConfirmDelete(row) : undefined}
          readOnly={readOnly}
          showCreatedBy={allReps}
          createdByNames={createdByNames}
        />

        <ProposalPagination
          currentPage={currentPage}
          totalItems={totalProposals}
          perPage={perPage}
          onPageChange={setCurrentPage}
          onPerPageChange={handlePerPageChange}
        />
      </MainArea>

      {!readOnly && (
        <ProposalCreationComponent
          isProposalModalOpen={isProposalModalOpen}
          handleProposalModalOpenState={handleProposalModalOpenState}
        />
      )}
      {confirmDelete && (
        <ModalOverlay onClick={() => setConfirmDelete(null)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Delete Proposal</ModalTitle>
            <ModalBody>
              Are you sure you want to delete the proposal for{' '}
              <strong>{confirmDelete.businessName}</strong>? This action cannot
              be undone.
            </ModalBody>
            <ModalActions>
              <ModalCancelBtn
                type="button"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </ModalCancelBtn>
              <ModalDangerBtn type="button" onClick={handleDeleteConfirm}>
                Delete
              </ModalDangerBtn>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}

      {confirmDuplicate && (
        <ModalOverlay onClick={() => setConfirmDuplicate(null)}>
          <DuplicateModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Duplicate Proposal</ModalTitle>
            <ModalOptionalNote>
              Optionally update the client details for the new proposal. All
              fields are optional — leave blank to keep the originals.
            </ModalOptionalNote>
            <ModalFormGrid>
              <FormField>
                <FormLabel htmlFor="dup-businessName">Business Name</FormLabel>
                <FormInput
                  id="dup-businessName"
                  type="text"
                  placeholder="Enter business name"
                  value={duplicateForm.businessName}
                  onChange={e =>
                    handleDuplicateFormChange('businessName', e.target.value)
                  }
                />
              </FormField>
              <FormField>
                <FormLabel htmlFor="dup-clientEmail">Email</FormLabel>
                <FormInput
                  id="dup-clientEmail"
                  type="email"
                  placeholder="Enter email"
                  value={duplicateForm.clientEmail}
                  onChange={e =>
                    handleDuplicateFormChange('clientEmail', e.target.value)
                  }
                />
              </FormField>
              <FormField>
                <FormLabel htmlFor="dup-clientPhone">Client Phone</FormLabel>
                <FormInput
                  id="dup-clientPhone"
                  type="tel"
                  placeholder="Enter phone number"
                  value={duplicateForm.clientPhone}
                  onChange={e =>
                    handleDuplicateFormChange('clientPhone', e.target.value)
                  }
                />
              </FormField>
              <FormField>
                <FormLabel htmlFor="dup-thryvId">Thryv ID</FormLabel>
                <FormInput
                  id="dup-thryvId"
                  type="text"
                  placeholder="Enter Thryv ID"
                  value={duplicateForm.thryvId}
                  onChange={e =>
                    handleDuplicateFormChange('thryvId', e.target.value)
                  }
                />
              </FormField>
              <FormField className="full-width">
                <FormLabel htmlFor="dup-clientType">Client Type</FormLabel>
                <FormSelect
                  id="dup-clientType"
                  value={duplicateForm.clientType}
                  onChange={e =>
                    handleDuplicateFormChange('clientType', e.target.value)
                  }
                >
                  <option value="">Select client type</option>
                  <option value="NEW">New</option>
                  <option value="EXISTING">Existing</option>
                </FormSelect>
              </FormField>
            </ModalFormGrid>
            <ModalActions>
              <ModalCancelBtn
                type="button"
                onClick={() => setConfirmDuplicate(null)}
              >
                Cancel
              </ModalCancelBtn>
              <ModalPrimaryBtn type="button" onClick={handleDuplicateConfirm}>
                Duplicate
              </ModalPrimaryBtn>
            </ModalActions>
          </DuplicateModalBox>
        </ModalOverlay>
      )}
    </ContentArea>
  )
}

export {ProposalToolDashboard}
